import React, { useEffect, useRef, useState } from 'react';
import { Hash, RefreshCw, Save, AlertTriangle, ChevronDown, ChevronUp, Lock } from 'lucide-react';
import { STRATEGIES, CASING_OPTIONS, TEMPLATE_TOKENS } from '../config/codeStrategies';
import ReusableDropdown from '../../../components/ReusableDropdown';
import { text } from '../constants/configurationText';
import { translateMessage } from '../constants/configurationText';

const CodePolicySection = ({
  theme,
  styles,
  t: L = text.en,
  clientName,
  policy,
  draft,
  loading,
  saving,
  isDirty,
  previewCodes,
  previewLoading,
  previewError,
  warnings,
  onUpdateDraft,
  onReset,
  onPreview,
  onSave,
}) => {
  const [showAdvanced, setShowAdvanced] = useState(draft.strategy === 'TEMPLATE');
  const debounceRef = useRef(null);

  // Debounced live preview: re-render sample codes ~400ms after the last
  // edit, rather than on every keystroke.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onPreview();
    }, 400);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.prefix, draft.separator, draft.suffix, draft.strategy, draft.bodyLength, draft.padWithZeros, draft.casing, draft.alphabet, draft.template, draft.startAt]);

  const cardStyle = styles.subCard;
  const labelStyle = styles.fieldLabel;
  const fieldStyle = styles.field;
  const pickerTheme = theme === 'light' ? 'light' : 'dark';
  const ddLang = L.lang;
  const brandSoft = theme === 'light' ? 'rgba(46,124,186,0.06)' : 'rgba(46,124,186,0.15)';

  const isNewPolicy = !policy;

  if (loading) {
    return (
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: styles.colors.muted }}>
          <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
          <span style={{ fontSize: '13.5px' }}>{L.loadingPolicy}</span>
        </div>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <div style={styles.subCardHeader}>
        <div style={styles.subCardTitleRow}>
          <span style={styles.subCardIcon}>
            <Hash size={18} />
          </span>
          <div>
            <h4 style={styles.subCardTitle}>
              {L.codeFormatTitle}
            </h4>
            <p style={styles.subCardSubtitle}>
              {isNewPolicy ? L.noPolicy : L.policyHint}
            </p>
          </div>
        </div>
        {policy?.isLocked && (
          <span style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            fontSize: '12px', fontWeight: '700', color: '#dc2626',
            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)',
            borderRadius: '999px', padding: '4px 10px', flexShrink: 0,
          }}>
            <Lock size={11} /> {L.locked}
          </span>
        )}
      </div>

      {warnings?.length > 0 && (
        <div style={{ ...styles.notice('warning'), marginBottom: '16px' }}>
          <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
          <div>
            {warnings.map((w, i) => <div key={i}>{w}</div>)}
          </div>
        </div>
      )}

      {/* Prefix / separator / suffix */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(160px, 100%), 1fr))', gap: '16px', marginBottom: '18px' }}>
        <div>
          <label style={labelStyle}>{L.prefix}</label>
          <input
            type="text"
            value={draft.prefix}
            onChange={(e) => onUpdateDraft('prefix', e.target.value.toUpperCase())}
            placeholder="RAY"
            maxLength={12}
            disabled={policy?.isLocked}
            className="cfg-input"
            style={fieldStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>{L.separator}</label>
          <ReusableDropdown
            items={[
              { id: '-', label: '-' },
              { id: '_', label: '_' },
              { id: '/', label: '/' },
              { id: '__none__', label: L.noneSep },
            ]}
            value={draft.separator === '' ? '__none__' : draft.separator}
            onChange={(value) => onUpdateDraft('separator', value === '__none__' ? '' : value)}
            getItemKey={(item) => item.id}
            getItemLabel={(item) => item.label}
            searchable={false}
            theme={pickerTheme}
            language={ddLang}
            inputStyle={fieldStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>{L.suffix}</label>
          <input
            type="text"
            value={draft.suffix || ''}
            onChange={(e) => onUpdateDraft('suffix', e.target.value)}
            placeholder={L.suffixPlaceholder}
            className="cfg-input"
            style={fieldStyle}
          />
        </div>
      </div>

      {/* Strategy chips */}
      <label style={labelStyle}>{L.format}</label>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(170px, 100%), 1fr))', gap: '10px', marginBottom: '18px' }}>
        {STRATEGIES.map((s) => {
          const isActive = draft.strategy === s.value;
          return (
            <div
              key={s.value}
              onClick={() => {
                if (policy?.isLocked) return;
                onUpdateDraft('strategy', s.value);
                setShowAdvanced(s.value === 'TEMPLATE');
              }}
              style={{
                border: isActive ? `1.5px solid ${styles.brand}` : `1px solid ${styles.colors.border}`,
                borderRadius: '12px',
                padding: '12px 14px',
                cursor: policy?.isLocked ? 'not-allowed' : 'pointer',
                opacity: policy?.isLocked && !isActive ? 0.5 : 1,
                background: isActive ? brandSoft : styles.colors.softBg,
                transition: 'all 0.15s',
              }}
            >
              <div style={{ fontSize: '13px', fontWeight: '700', color: isActive ? styles.colors.chipText : styles.colors.body }}>
                {L.strategies[s.value]?.label || s.label}
              </div>
              <div style={{ fontSize: '11.5px', color: styles.colors.faint, margin: '3px 0 8px 0' }}>
                {L.strategies[s.value]?.hint || s.hint}
              </div>
              <code style={{ ...styles.codeChip, fontSize: '11.5px', padding: '3px 8px' }}>
                {s.sample(draft.prefix, draft.separator)}
              </code>
            </div>
          );
        })}
      </div>

      {/* Sequential / random controls */}
      {draft.strategy !== 'TEMPLATE' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(160px, 100%), 1fr))', gap: '16px', marginBottom: '16px' }}>
          <div>
            <label style={labelStyle}>{draft.strategy === 'SEQUENTIAL' ? L.digits : L.length}</label>
            <input
              type="number"
              min={1}
              max={20}
              value={draft.bodyLength}
              onChange={(e) => onUpdateDraft('bodyLength', Number(e.target.value))}
              className="cfg-input"
            style={fieldStyle}
            />
          </div>
          {draft.strategy === 'SEQUENTIAL' && (
            <>
              <div>
                <label style={labelStyle}>{L.zeroPadded}</label>
                <ReusableDropdown
                  items={[
                    { id: 'yes', label: L.padYes },
                    { id: 'no', label: L.padNo },
                  ]}
                  value={draft.padWithZeros ? 'yes' : 'no'}
                  onChange={(value) => onUpdateDraft('padWithZeros', value === 'yes')}
                  getItemKey={(item) => item.id}
                  getItemLabel={(item) => item.label}
                  searchable={false}
                  theme={pickerTheme}
                  language={ddLang}
                  inputStyle={fieldStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>{L.startAt}</label>
                <input
                  type="number"
                  min={0}
                  value={draft.startAt}
                  onChange={(e) => onUpdateDraft('startAt', Number(e.target.value))}
                  className="cfg-input"
            style={fieldStyle}
                />
              </div>
            </>
          )}
          {draft.strategy !== 'SEQUENTIAL' && (
            <div>
              <label style={labelStyle}>{L.casing}</label>
              <ReusableDropdown
                items={CASING_OPTIONS}
                value={draft.casing}
                onChange={(value) => onUpdateDraft('casing', value)}
                getItemKey={(c) => c}
                getItemLabel={(c) => L.casingOptions[c] || c}
                searchable={false}
                theme={pickerTheme}
                language={ddLang}
                inputStyle={fieldStyle}
              />
            </div>
          )}
        </div>
      ) : (
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>{L.template}</label>
          <input
            type="text"
            value={draft.template || ''}
            onChange={(e) => onUpdateDraft('template', e.target.value)}
            placeholder="{PREFIX}-{YY}-{SEQ:4}"
            className="cfg-input"
            style={{ ...fieldStyle, fontFamily: 'monospace' }}
          />
        </div>
      )}

      {/* Advanced toggle (casing when sequential, alphabet override, template legend) */}
      <button
        type="button"
        onClick={() => setShowAdvanced((v) => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          background: 'none', border: 'none', cursor: 'pointer',
          color: styles.colors.chipText,
          fontSize: '13px', fontWeight: '700', padding: 0, marginBottom: showAdvanced ? '12px' : '16px',
        }}
      >
        {showAdvanced ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        {L.advanced}
      </button>

      {showAdvanced && (
        <div style={{
          background: styles.colors.softBg, border: `1px solid ${styles.colors.border}`,
          borderRadius: '12px', padding: '14px', marginBottom: '16px',
        }}>
          {draft.strategy === 'SEQUENTIAL' && (
            <div style={{ marginBottom: '10px' }}>
              <label style={labelStyle}>{L.casing}</label>
              <ReusableDropdown
                items={CASING_OPTIONS}
                value={draft.casing}
                onChange={(value) => onUpdateDraft('casing', value)}
                getItemKey={(c) => c}
                getItemLabel={(c) => L.casingOptions[c] || c}
                searchable={false}
                theme={pickerTheme}
                language={ddLang}
                inputStyle={fieldStyle}
              />
            </div>
          )}
          {draft.strategy.startsWith('RANDOM_') && (
            <div style={{ marginBottom: '10px' }}>
              <label style={labelStyle}>{L.alphabet}</label>
              <input
                type="text"
                value={draft.alphabet || ''}
                onChange={(e) => onUpdateDraft('alphabet', e.target.value)}
                placeholder={L.alphabetPlaceholder}
                className="cfg-input"
            style={fieldStyle}
              />
            </div>
          )}
          {draft.strategy === 'TEMPLATE' && (
            <div>
              <div style={{ ...labelStyle, marginBottom: '8px' }}>
                {L.tokens}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px' }}>
                {TEMPLATE_TOKENS.map((tok) => (
                  <div key={tok.token} style={{ fontSize: '12px', color: styles.colors.muted }}>
                    <code style={{ fontWeight: '700', color: styles.colors.text }}>{tok.token}</code> — {L.tokenDescriptions[tok.token] || tok.desc}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Live preview */}
      <div style={{
        background: brandSoft,
        border: '1px solid rgba(46,124,186,0.25)',
        borderRadius: '12px', padding: '14px', marginBottom: '16px',
      }}>
        <div style={{ ...labelStyle, color: styles.colors.chipText, marginBottom: '10px' }}>
          {L.nextCodes}
        </div>
        {previewLoading ? (
          <span style={{ fontSize: '12px', opacity: 0.6 }}>{L.generating}</span>
        ) : previewError ? (
          <span style={{ fontSize: '12px', color: '#dc2626' }}>{translateMessage(previewError, ddLang)}</span>
        ) : previewCodes.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {previewCodes.map((code, i) => (
              <code key={i} style={styles.codeChip}>
                {code}
              </code>
            ))}
          </div>
        ) : (
          <span style={{ fontSize: '12px', opacity: 0.5 }}>{L.enterPrefix}</span>
        )}
      </div>

      {/* Footer stats + actions */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px',
        paddingTop: '16px', borderTop: `1px solid ${styles.colors.rowBorder}`,
      }}>
        <div style={{ fontSize: '12.5px', color: styles.colors.muted }}>
          {policy
            ? <>{L.issued} <strong>{policy.issuedCount ?? 0}</strong> · {L.last} <strong>{policy.lastIssuedCode || L.na}</strong> · {L.nextSeq} <strong>{policy.nextSequence}</strong></>
            : L.neverPolicy}
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {isDirty && (
            <button
              type="button"
              onClick={onReset}
              className="cfg-btn-secondary"
              style={styles.btnSecondary()}
            >
              {L.discard}
            </button>
          )}
          <button
            type="button"
            onClick={() => onSave(clientName)}
            disabled={saving || !draft.prefix?.trim() || !isDirty}
            className="cfg-btn-primary"
            style={styles.btnPrimary(saving || !draft.prefix?.trim() || !isDirty)}
          >
            {saving ? <RefreshCw size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={15} />}
            {saving ? L.savingShort : L.saveFormat}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CodePolicySection;
